import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Card,
  CardContent,
  Collapse,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Api as ApiIcon,
  SmartToy as SmartToyIcon,
  Palette as PaletteIcon,
  Storage as StorageIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import {
  settingsService,
  UserSettings,
} from "../services/settings/settingsService";
import { onboardingService } from "../services/onboarding/onboardingService";
import IntegrationsSettings from "../components/integrations/IntegrationsSettings";
import LocalAgentSettings from "../components/integrations/LocalAgentSettings";
import PrimeAgentSettings from "../components/integrations/PrimeAgentSettings";
import { useAuth } from "../contexts/AuthContext";

interface SettingsData {
  // API Settings
  plaidEnvironment: "sandbox" | "production";

  // Application Settings
  defaultTransactionDays: number;
  currency: string;
  dateFormat: string;
  timezone: string;

  // UI Settings
  theme: "light" | "dark" | "auto";
  language: string;
  compactMode: boolean;

  // Data Settings
  autoBackup: boolean;
  dataRetentionDays: number;

  // Security Settings
  sessionTimeout: number;
  requireConfirmation: boolean;
  discoverable: boolean;

  // Tax Category Settings
  federalTaxCategories: string;
  stateTaxCategories: string;
}

const defaultSettings: SettingsData = {
  plaidEnvironment: "sandbox",
  defaultTransactionDays: 90,
  currency: "USD",
  dateFormat: "MM/DD/YYYY",
  timezone: "America/Los_Angeles",
  theme: "light",
  language: "en",
  compactMode: false,
  autoBackup: true,
  dataRetentionDays: 365,
  sessionTimeout: 30,
  requireConfirmation: true,
  discoverable: false,
  federalTaxCategories:
    "Business Income,Business Expense,Home Office,Travel,Meals,Equipment,Supplies,Marketing,Professional Services,Software Subscriptions,Insurance",
  stateTaxCategories:
    "State Income,State Deductions,State Business Expense,State Travel,State Equipment,Professional License,State Tax Payments,Local Tax,Property Tax,Sales Tax",
};

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { user } = useAuth();
  const [settings, setSettings] = useState<SettingsData>(defaultSettings);
  const [originalSettings, setOriginalSettings] =
    useState<SettingsData>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "success" | "error"
  >("idle");
  const [expandedSections, setExpandedSections] = useState({
    api: true,
    app: false,
    ui: false,
    data: false,
    security: false,
    integrations: false,
    "prime-agent": false,
    localAgent: false,
    taxCategories: false,
  });

  // Convert UserSettings to SettingsData format
  const convertFromUserSettings = (
    userSettings: UserSettings
  ): SettingsData => {
    return {
      plaidEnvironment: userSettings.plaid_environment,
      defaultTransactionDays: userSettings.default_transaction_days,
      currency: userSettings.currency,
      dateFormat: userSettings.date_format,
      timezone: userSettings.timezone,
      theme: userSettings.theme,
      language: userSettings.language,
      compactMode: userSettings.compact_mode,
      autoBackup: userSettings.auto_backup,
      dataRetentionDays: userSettings.data_retention_days,
      sessionTimeout: userSettings.session_timeout,
      requireConfirmation: userSettings.require_confirmation,
      discoverable: userSettings.discoverable,
      federalTaxCategories: userSettings.federal_tax_categories,
      stateTaxCategories: userSettings.state_tax_categories,
    };
  };

  // Convert SettingsData to UserSettings format
  const convertToUserSettings = (
    settings: SettingsData
  ): Partial<UserSettings> => {
    return {
      plaid_environment: settings.plaidEnvironment,
      default_transaction_days: settings.defaultTransactionDays,
      currency: settings.currency,
      date_format: settings.dateFormat,
      timezone: settings.timezone,
      theme: settings.theme,
      language: settings.language,
      compact_mode: settings.compactMode,
      auto_backup: settings.autoBackup,
      data_retention_days: settings.dataRetentionDays,
      session_timeout: settings.sessionTimeout,
      require_confirmation: settings.requireConfirmation,
      discoverable: settings.discoverable,
      federal_tax_categories: settings.federalTaxCategories,
      state_tax_categories: settings.stateTaxCategories,
    };
  };

  const loadSettings = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Migrate localStorage settings if they exist
      await settingsService.migrateLocalStorageSettings();

      // Load settings from Supabase
      const userSettings = await settingsService.getUserSettings();
      const convertedSettings = convertFromUserSettings(userSettings);

      setSettings(convertedSettings);
      setOriginalSettings(convertedSettings);

      // Also update localStorage for backward compatibility with other components
      // that might still be reading from localStorage
      const localStorageFormat = {
        plaidEnvironment: convertedSettings.plaidEnvironment,
        defaultTransactionDays: convertedSettings.defaultTransactionDays,
        currency: convertedSettings.currency,
        dateFormat: convertedSettings.dateFormat,
        timezone: convertedSettings.timezone,
        theme: convertedSettings.theme,
        language: convertedSettings.language,
        compactMode: convertedSettings.compactMode,
        autoBackup: convertedSettings.autoBackup,
        dataRetentionDays: convertedSettings.dataRetentionDays,
        sessionTimeout: convertedSettings.sessionTimeout,
        requireConfirmation: convertedSettings.requireConfirmation,
        discoverable: convertedSettings.discoverable,
        federalTaxCategories: convertedSettings.federalTaxCategories,
        stateTaxCategories: convertedSettings.stateTaxCategories,
      };
      localStorage.setItem("appSettings", JSON.stringify(localStorageFormat));
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    if (!user) {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
      return;
    }

    setSaveStatus("saving");
    try {
      const userSettings = convertToUserSettings(settings);
      const result = await settingsService.saveUserSettings(userSettings);

      if (result.success) {
        setOriginalSettings(settings);
        setSaveStatus("success");

        // Update localStorage for backward compatibility
        const localStorageFormat = {
          plaidEnvironment: settings.plaidEnvironment,
          defaultTransactionDays: settings.defaultTransactionDays,
          currency: settings.currency,
          dateFormat: settings.dateFormat,
          timezone: settings.timezone,
          theme: settings.theme,
          language: settings.language,
          compactMode: settings.compactMode,
          autoBackup: settings.autoBackup,
          dataRetentionDays: settings.dataRetentionDays,
          sessionTimeout: settings.sessionTimeout,
          requireConfirmation: settings.requireConfirmation,
          discoverable: settings.discoverable,
          federalTaxCategories: settings.federalTaxCategories,
          stateTaxCategories: settings.stateTaxCategories,
        };
        localStorage.setItem("appSettings", JSON.stringify(localStorageFormat));

        setTimeout(() => setSaveStatus("idle"), 3000);
      } else {
        setSaveStatus("error");
        setTimeout(() => setSaveStatus("idle"), 5000);
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 5000);
    }
  };

  const resetSettings = async () => {
    if (
      window.confirm(
        "Are you sure you want to reset all settings to defaults? This action cannot be undone."
      )
    ) {
      if (user) {
        await settingsService.clearUserSettings();
      }
      setSettings(defaultSettings);
      setOriginalSettings(defaultSettings);
    }
  };

  const discardChanges = () => {
    setSettings(originalSettings);
  };

  const hasChanges =
    JSON.stringify(settings) !== JSON.stringify(originalSettings);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleInputChange = (field: keyof SettingsData, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const SettingCard = ({
    title,
    icon,
    section,
    children,
  }: {
    title: string;
    icon: React.ReactNode;
    section: keyof typeof expandedSections;
    children: React.ReactNode;
  }) => (
    <Card
      sx={{
        mb: 2,
        backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
        backgroundImage: isDarkMode
          ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
          : "none",
        boxShadow: isDarkMode
          ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
          : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => toggleSection(section)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {icon}
            <Typography variant="h6">{title}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {expandedSections[section] ? "Collapse" : "Expand"}
          </Typography>
        </Box>
        <Collapse in={expandedSections[section]}>
          <Box sx={{ mt: 2 }}>{children}</Box>
        </Collapse>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">Please sign in to access settings.</Alert>
      </Container>
    );
  }

  // Get the current Client ID based on selected environment

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 3,
          gap: 2,
        }}
      >
        {hasChanges && (
          <Button
            variant="outlined"
            onClick={discardChanges}
            disabled={saveStatus === "saving"}
          >
            Discard Changes
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={saveSettings}
          disabled={!hasChanges || saveStatus === "saving"}
        >
          {saveStatus === "saving" ? "Saving..." : "Save Settings"}
        </Button>
      </Box>

      {saveStatus === "success" && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      {saveStatus === "error" && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to save settings. Please try again.
        </Alert>
      )}

      {/* API Settings */}
      <SettingCard
        title="API Configuration"
        icon={<ApiIcon color="primary" />}
        section="api"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Plaid Environment</InputLabel>
            <Select
              value={settings.plaidEnvironment}
              label="Plaid Environment"
              onChange={(e) =>
                handleInputChange("plaidEnvironment", e.target.value)
              }
            >
              <MenuItem value="sandbox">Sandbox (Development)</MenuItem>
              <MenuItem value="production">Production (Live)</MenuItem>
            </Select>
          </FormControl>

          <Alert severity="info">
            <Typography variant="body2">
              Plaid credentials are managed at the system level. The environment
              setting above controls which mode the system uses for all bank
              connections.
            </Typography>
          </Alert>
        </Box>
      </SettingCard>

      {/* Application Settings */}
      <SettingCard
        title="Application Settings"
        icon={<SettingsIcon color="primary" />}
        section="app"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              label="Default Transaction Period (days)"
              type="number"
              value={settings.defaultTransactionDays}
              onChange={(e) =>
                handleInputChange(
                  "defaultTransactionDays",
                  parseInt(e.target.value)
                )
              }
              inputProps={{ min: 1, max: 365 }}
              helperText="How many days of transactions to load by default"
            />

            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={settings.currency}
                label="Currency"
                onChange={(e) => handleInputChange("currency", e.target.value)}
              >
                <MenuItem value="USD">USD ($)</MenuItem>
                <MenuItem value="EUR">EUR (€)</MenuItem>
                <MenuItem value="GBP">GBP (£)</MenuItem>
                <MenuItem value="CAD">CAD (C$)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <FormControl fullWidth>
              <InputLabel>Date Format</InputLabel>
              <Select
                value={settings.dateFormat}
                label="Date Format"
                onChange={(e) =>
                  handleInputChange("dateFormat", e.target.value)
                }
              >
                <MenuItem value="MM/DD/YYYY">MM/DD/YYYY (US)</MenuItem>
                <MenuItem value="DD/MM/YYYY">DD/MM/YYYY (EU)</MenuItem>
                <MenuItem value="YYYY-MM-DD">YYYY-MM-DD (ISO)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={settings.timezone}
                label="Timezone"
                onChange={(e) => handleInputChange("timezone", e.target.value)}
              >
                <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                <MenuItem value="America/Denver">Mountain Time</MenuItem>
                <MenuItem value="America/Chicago">Central Time</MenuItem>
                <MenuItem value="America/New_York">Eastern Time</MenuItem>
                <MenuItem value="UTC">UTC</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </SettingCard>

      {/* UI Settings */}
      <SettingCard
        title="User Interface"
        icon={<PaletteIcon color="primary" />}
        section="ui"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <FormControl fullWidth>
              <InputLabel>Theme</InputLabel>
              <Select
                value={settings.theme}
                label="Theme"
                onChange={(e) => handleInputChange("theme", e.target.value)}
              >
                <MenuItem value="light">Light</MenuItem>
                <MenuItem value="dark">Dark</MenuItem>
                <MenuItem value="auto">Auto (System)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Language</InputLabel>
              <Select
                value={settings.language}
                label="Language"
                onChange={(e) => handleInputChange("language", e.target.value)}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="es">Spanish</MenuItem>
                <MenuItem value="fr">French</MenuItem>
                <MenuItem value="de">German</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body1">Compact Mode</Typography>
              <Typography variant="body2" color="text.secondary">
                Use smaller spacing and condensed layouts
              </Typography>
            </Box>
            <Switch
              checked={settings.compactMode}
              onChange={(e) =>
                handleInputChange("compactMode", e.target.checked)
              }
            />
          </Box>
        </Box>
      </SettingCard>

      {/* Data Management */}
      <SettingCard
        title="Data Management"
        icon={<StorageIcon color="primary" />}
        section="data"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body1">Automatic Backup</Typography>
              <Typography variant="body2" color="text.secondary">
                Automatically backup your data to browser storage
              </Typography>
            </Box>
            <Switch
              checked={settings.autoBackup}
              onChange={(e) =>
                handleInputChange("autoBackup", e.target.checked)
              }
            />
          </Box>

          <TextField
            fullWidth
            label="Data Retention Period (days)"
            type="number"
            value={settings.dataRetentionDays}
            onChange={(e) =>
              handleInputChange("dataRetentionDays", parseInt(e.target.value))
            }
            inputProps={{ min: 30, max: 3650 }}
            helperText="How long to keep transaction data"
            sx={{ maxWidth: 300 }}
          />
        </Box>
      </SettingCard>

      {/* Security Settings */}
      <SettingCard
        title="Security & Privacy"
        icon={<SecurityIcon color="primary" />}
        section="security"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <TextField
            fullWidth
            label="Session Timeout (minutes)"
            type="number"
            value={settings.sessionTimeout}
            onChange={(e) =>
              handleInputChange("sessionTimeout", parseInt(e.target.value))
            }
            inputProps={{ min: 5, max: 480 }}
            helperText="Auto-logout after inactivity"
            sx={{ maxWidth: 300 }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body1">Require Confirmation</Typography>
              <Typography variant="body2" color="text.secondary">
                Ask for confirmation before destructive actions
              </Typography>
            </Box>
            <Switch
              checked={settings.requireConfirmation}
              onChange={(e) =>
                handleInputChange("requireConfirmation", e.target.checked)
              }
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body1">Discoverable</Typography>
              <Typography variant="body2" color="text.secondary">
                Allow others to find you when inviting to groups
              </Typography>
            </Box>
            <Switch
              checked={settings.discoverable}
              onChange={(e) =>
                handleInputChange("discoverable", e.target.checked)
              }
            />
          </Box>
        </Box>
      </SettingCard>

      {/* Integrations */}
      <SettingCard
        title="Integrations"
        icon={<ApiIcon color="primary" />}
        section="integrations"
      >
        <IntegrationsSettings />
      </SettingCard>

      {/* Prime Agent Direct Connect */}
      <SettingCard
        title="Prime Agent Direct Connect"
        icon={<AccountBalanceIcon color="primary" />}
        section="prime-agent"
      >
        <PrimeAgentSettings />
      </SettingCard>

      {/* Local AI agent (OpenClaw etc.) */}
      <SettingCard
        title="Connect a local agent"
        icon={<SmartToyIcon color="primary" />}
        section="localAgent"
      >
        <LocalAgentSettings />
      </SettingCard>

      {/* Tax Categories Settings */}
      <SettingCard
        title="Tax Categories"
        icon={<ReceiptIcon color="primary" />}
        section="taxCategories"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <TextField
            fullWidth
            label="Federal Tax Categories"
            multiline
            rows={4}
            value={settings.federalTaxCategories}
            onChange={(e) =>
              handleInputChange("federalTaxCategories", e.target.value)
            }
            helperText="Enter comma-separated federal tax categories"
            placeholder="Business Income, Business Expense, Travel, Equipment..."
          />

          <TextField
            fullWidth
            label="State Tax Categories"
            multiline
            rows={4}
            value={settings.stateTaxCategories}
            onChange={(e) =>
              handleInputChange("stateTaxCategories", e.target.value)
            }
            helperText="Enter comma-separated state tax categories"
            placeholder="State Income, State Deductions, Property Tax, Sales Tax..."
          />
        </Box>
      </SettingCard>

      {/* Onboarding Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h6">Getting Started Guide</Typography>
              <Typography variant="body2" color="text.secondary">
                Run the getting started guide again to reconfigure your account
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={async () => {
                await onboardingService.resetOnboarding();
                window.location.reload();
              }}
            >
              Restart Guide
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Reset Section */}
      <Card sx={{ mb: 2, bgcolor: "error.main", color: "error.contrastText" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h6">Reset All Settings</Typography>
              <Typography variant="body2">
                This will restore all settings to their default values
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="error"
              startIcon={<RefreshIcon />}
              onClick={resetSettings}
            >
              Reset to Defaults
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SettingsPage;
